import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, ImageSourcePropType, Platform, StyleSheet, Text, View } from 'react-native';

import { JackpotChipPng, SmallVipSix } from '../../../assets';
import { IconCloseSvg, TabInventorySvg } from '../../../assets/inventory';
import Coins1500k from '../../../assets/storeModal/1,5m-coins.png';
import Coins20k from '../../../assets/storeModal/20k-coins.png';
import Coins42k from '../../../assets/storeModal/42k-coins.png';
import Coins99k from '../../../assets/storeModal/99k-coins.png';
import Coins230k from '../../../assets/storeModal/230k-coins.png';
import Coins500k from '../../../assets/storeModal/500k-coins.png';
import StoreDivider from '../../../assets/storeModal/divider.png';
import FreeCoins from '../../../assets/storeModal/free-coins.png';
import StoreIcon from '../../../assets/storeModal/store.png';
import VipBackground from '../../../assets/storeModal/vipClub/background.png';
import { goldGlow } from '../../../shared/glowStyles';
import { Package } from '../../../store/types';
import { useAdReward } from '../../../utils/hooks/useAdReward';
import { useDevice } from '../../../utils/hooks/useDevice';
import { CtaButton } from '../common/CtaButton';
import { RedesignScrollView } from '../common/RedesignScrollView';
import { SHOP_SCROLL_INDICATOR } from '../common/ScrollIndicator';
import { Pressable } from '../common/Touchable';
import { INVENTORY_INTRO, INVENTORY_ITEMS } from '../Inventory/inventoryCatalog';
import InventoryCategories, { InventoryCategoryKey } from '../Inventory/InventoryCategories';
import InventoryItemCard from '../Inventory/InventoryItemCard';
import RedesignModal from '../Modals/RedesignModal';
import { useRedesignScale } from '../Modals/useRedesignScale';
import StoreVipContent from './StoreVipContent';

const STORE_LAYOUT = {
	desktopPanelHeight: 640,
	desktopPanelWidth: 640,
	desktopScaleHeight: 768,
	dividerHeight: 12,
	dividerMarginBottom: 10,
	dividerMarginTop: 2,
	headerHeight: 66,
	listPadding: 12,
	panelHeight: 690,
	packageGap: 10,
	packageHeight: 104,
	scrollMarginTop: 10,
	tabsHeight: 36,
} as const;

const TAB_ICON_SIZE = 18;

const PACKAGE_ARTWORK: Array<{ amount: number; source: ImageSourcePropType }> = [
	{ amount: 0, source: FreeCoins },
	{ amount: 20000, source: Coins20k },
	{ amount: 42000, source: Coins42k },
	{ amount: 99000, source: Coins99k },
	{ amount: 230000, source: Coins230k },
	{ amount: 500000, source: Coins500k },
	{ amount: 1500000, source: Coins1500k },
];

type Props = {
	chipPackages: Package[];
	vipPackages: Package[];
	initialTab?: 'chips' | 'vip';
	loading: boolean;
	error?: string;
	onBuy: (pkg: Package) => void;
	onClose: () => void;
	onGoToClub: () => void;
	renderInline?: boolean;
};

export const formatAmount = (value: number) => Math.round(value).toLocaleString('en-US');

export const formatPrice = (pkg: Package) => `€${Number(pkg.price).toFixed(2)}`;

export const getArtwork = (pkg: Package) => {
	const amount = pkg.chipAmount ?? pkg.chips ?? 0;
	return PACKAGE_ARTWORK.reduce((closest, item) =>
		Math.abs(item.amount - amount) < Math.abs(closest.amount - amount) ? item : closest,
	).source;
};

const StoreRedesignModal = ({
	chipPackages,
	vipPackages,
	initialTab = 'chips',
	loading,
	error,
	onBuy,
	onClose,
	onGoToClub,
	renderInline = false,
}: Props) => {
	const { t } = useTranslation();
	const { isDesktop } = useDevice();
	const isDesktopWeb = Platform.OS === 'web' && isDesktop;
	const scale = useRedesignScale({
		fitHeight: isDesktop ? STORE_LAYOUT.desktopScaleHeight : undefined,
		panelWidth: isDesktop ? STORE_LAYOUT.desktopPanelWidth : undefined,
	});
	const [tab, setTab] = useState<'chips' | 'vip' | 'inventory'>(initialTab);
	const [category, setCategory] = useState<InventoryCategoryKey>('frame');
	// The tabs' and buttons' type and the tab icons were drawn at their phone sizes, which read tiny
	// once the desktop scales the panel up around them; on a phone they stay as they were.
	const tabScale = isDesktop ? scale : 1;
	const inventoryScale = isDesktop ? scale * 1.35 : scale;
	const { chips: adRewardChips, handlePress: watchAd } = useAdReward();
	const packages = useMemo(
		() =>
			chipPackages.map(pkg => ({
				pkg,
				amount: pkg.chipAmount ?? pkg.chips ?? 0,
				artwork: getArtwork(pkg),
			})),
		[chipPackages],
	);
	const inventoryItems = INVENTORY_ITEMS[category];
	const inventoryIntro = INVENTORY_INTRO[category];

	return (
		<RedesignModal
			onClose={onClose}
			renderInline={renderInline}
			scale={scale}
			panel={{
				height: isDesktop ? STORE_LAYOUT.desktopPanelHeight : STORE_LAYOUT.panelHeight,
				width: isDesktop ? STORE_LAYOUT.desktopPanelWidth : undefined,
				radius: isDesktop ? 20 : 16,
				tint: 'rgba(54, 8, 2, 0.48)',
				overScreen: true,
				scrimColor: 'rgba(0,0,0,0.7)',
			}}
		>
			<View style={styles.root}>
				{tab === 'vip' ? (
					<Image source={VipBackground} resizeMode='contain' style={styles.vipBackground} />
				) : null}
				<View style={[styles.header, { height: STORE_LAYOUT.headerHeight * scale }]}>
					<View style={styles.headerTitle}>
						<Image
							source={StoreIcon}
							resizeMode='contain'
							style={{ width: 58 * scale, height: 58 * scale }}
						/>
						<Text
							style={[
								styles.title,
								// goldGlow is a view shadow: native draws it around the glyphs, but the web
								// turns it into a box-shadow and drew a dark box around the word. The web
								// gets the same orange bloom as a text shadow instead.
								Platform.OS === 'web'
									? { ...styles.titleWebGlow, textShadowRadius: 12 * scale }
									: goldGlow,
								{ fontSize: 36 * scale, lineHeight: 40 * scale },
							]}
						>
							{t('T_SITE_STORE_TITLE', { defaultValue: 'Store' })}
						</Text>
					</View>
					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_STORE_CLOSE', { defaultValue: 'Close store' })}
						onPress={onClose}
						style={[styles.close, { right: 20 * scale }]}
					>
						<IconCloseSvg width={18 * scale} height={18 * scale} />
					</Pressable>
				</View>
				<Image
					source={StoreDivider}
					resizeMode='stretch'
					style={{
						width: '100%',
						height: STORE_LAYOUT.dividerHeight * scale,
						marginTop: STORE_LAYOUT.dividerMarginTop * scale,
						marginBottom: STORE_LAYOUT.dividerMarginBottom * scale,
					}}
				/>

				<View
					style={[
						styles.tabs,
						{
							marginHorizontal: 14 * scale,
							height: STORE_LAYOUT.tabsHeight * scale,
							gap: 6 * scale,
						},
					]}
				>
					<StoreTab
						active={tab === 'chips'}
						icon={<Image source={JackpotChipPng} style={tabIconStyle(tabScale)} />}
						label={t('T_SITE_RANKING_CHIPS', { defaultValue: 'Chips' })}
						scale={tabScale}
						onPress={() => setTab('chips')}
					/>
					<StoreTab
						active={tab === 'vip'}
						icon={<Image source={SmallVipSix} style={tabIconStyle(tabScale)} />}
						label={t('T_SITE_VIP', { defaultValue: 'VIP' })}
						scale={tabScale}
						onPress={() => setTab('vip')}
					/>
					<StoreTab
						active={tab === 'inventory'}
						icon={<TabInventorySvg width={18 * scale} height={18 * scale} />}
						label={t('T_SITE_STORE_INVENTORY', { defaultValue: 'Inventory' })}
						scale={tabScale}
						onPress={() => setTab('inventory')}
					/>
				</View>

				{tab === 'chips' ? (
					<RedesignScrollView
						style={styles.scroll}
						contentContainerStyle={[
							styles.packageList,
							isDesktopWeb && styles.desktopPackageList,
							{
								padding: STORE_LAYOUT.listPadding * scale,
								rowGap: STORE_LAYOUT.packageGap * scale,
								columnGap: isDesktopWeb ? STORE_LAYOUT.packageGap * scale : undefined,
							},
						]}
						indicatorInsets={{ right: 5 * scale, top: 8 * scale, bottom: 8 * scale }}
						reserveGutter={false}
						indicatorTheme={SHOP_SCROLL_INDICATOR}
					>
						<View style={isDesktopWeb && styles.desktopPackageCell}>
							<FreePackageRow
								amount={adRewardChips > 0 ? adRewardChips : 10000}
								scale={scale}
								labelScale={tabScale}
								onWatchAd={watchAd}
							/>
						</View>
						{loading ? (
							<Text style={[styles.stateText, { fontSize: 14 * scale }]}>
								{t('T_SITE_STORE_LOADING_PACKAGES', { defaultValue: 'Loading packages...' })}
							</Text>
						) : null}
						{!loading && error ? (
							<Text style={[styles.errorText, { fontSize: 14 * scale }]}>{error}</Text>
						) : null}
						{!loading && !error
							? packages
									.filter(({ pkg }) => Number(pkg.price) > 0)
									.map(({ pkg, amount, artwork }) => (
										<View key={pkg.id} style={isDesktopWeb && styles.desktopPackageCell}>
											<PackageRow
												pkg={pkg}
												amount={amount}
												artwork={artwork}
												scale={scale}
												labelScale={tabScale}
												onBuy={onBuy}
											/>
										</View>
									))
							: null}
					</RedesignScrollView>
				) : tab === 'inventory' ? (
					<View style={styles.inventoryBody}>
						<InventoryCategories value={category} onChange={setCategory} scale={scale} />
						<Text
							numberOfLines={1}
							style={[styles.inventoryTitle, { fontSize: 22 * scale, lineHeight: 28.6 * scale }]}
						>
							{inventoryIntro.title}
						</Text>
						<Text
							numberOfLines={1}
							style={[styles.inventorySubtitle, { fontSize: 14 * scale, lineHeight: 14 * scale }]}
						>
							{inventoryIntro.subtitle}
						</Text>
						<RedesignScrollView
							style={styles.inventoryScroll}
							contentContainerStyle={[
								styles.inventoryGrid,
								isDesktop && styles.desktopInventoryGrid,
								{ gap: 8 * scale, width: isDesktop ? '100%' : 334 * scale },
							]}
							reserveGutter={false}
							indicatorTheme={SHOP_SCROLL_INDICATOR}
						>
							{inventoryItems.map(item => (
								<InventoryItemCard key={item.key} item={item} scale={inventoryScale} />
							))}
						</RedesignScrollView>
					</View>
				) : (
					<StoreVipContent
						isDesktop={isDesktop}
						scale={scale}
						vipPackages={vipPackages}
						onBuy={onBuy}
						onGoToClub={onGoToClub}
					/>
				)}
			</View>
		</RedesignModal>
	);
};

const tabIconStyle = (scale: number) => [
	styles.tabIcon,
	{ width: TAB_ICON_SIZE * scale, height: TAB_ICON_SIZE * scale },
];

const StoreTab = ({
	active = false,
	icon,
	label,
	scale,
	onPress,
}: {
	active?: boolean;
	icon: React.ReactNode;
	label: string;
	scale: number;
	onPress?: () => void;
}) => (
	<Pressable
		accessibilityRole='tab'
		accessibilityState={{ disabled: !onPress, selected: active }}
		disabled={!onPress}
		onPress={onPress}
		style={[
			styles.tab,
			active && styles.activeTab,
			{ borderRadius: 7 * scale, columnGap: 5 * scale },
		]}
	>
		{icon}
		<Text style={[styles.tabLabel, active && styles.activeTabLabel, { fontSize: 11 * scale }]}>
			{label}
		</Text>
	</Pressable>
);

const FreePackageRow = ({
	amount,
	scale,
	labelScale,
	onWatchAd,
}: {
	amount: number;
	scale: number;
	labelScale: number;
	onWatchAd: () => void;
}) => {
	const { t } = useTranslation();

	return (
		<View
			style={[
				styles.packageRow,
				{
					minHeight: STORE_LAYOUT.packageHeight * scale,
					borderRadius: 10 * scale,
					padding: 8 * scale,
				},
			]}
		>
			<Image
				source={FreeCoins}
				resizeMode='contain'
				style={{ width: 112 * scale, height: 88 * scale }}
			/>
			<View style={[styles.packageDetails, { rowGap: 10 * scale }]}>
				<View style={styles.amountRow}>
					<Image
						source={JackpotChipPng}
						resizeMode='contain'
						style={{ width: 28 * scale, height: 28 * scale }}
					/>
					<Text style={[styles.amount, { fontSize: 28 * scale }]}>{formatAmount(amount)}</Text>
				</View>
				<CtaButton
					accessibilityLabel={t('T_SITE_STORE_WATCH_AD_FOR', {
						defaultValue: 'Watch an ad for {{amount}} chips',
						amount: formatAmount(amount),
					})}
					label={t('T_SITE_SHOP_FREE', { defaultValue: 'FREE' }).toUpperCase()}
					onPress={onWatchAd}
					style={{ width: 168 * scale, minHeight: 36 * scale }}
					labelStyle={{ fontSize: 13 * labelScale }}
				/>
			</View>
		</View>
	);
};

const PackageRow = ({
	pkg,
	amount,
	artwork,
	scale,
	labelScale,
	onBuy,
}: {
	pkg: Package;
	amount: number;
	artwork: ImageSourcePropType;
	scale: number;
	labelScale: number;
	onBuy: (pkg: Package) => void;
}) => {
	const { t } = useTranslation();

	return (
		<View
			style={[
				styles.packageRow,
				{
					minHeight: STORE_LAYOUT.packageHeight * scale,
					borderRadius: 10 * scale,
					padding: 8 * scale,
				},
			]}
		>
			<Image
				source={artwork}
				resizeMode='contain'
				style={{ width: 112 * scale, height: 88 * scale }}
			/>
			<View style={[styles.packageDetails, { rowGap: 10 * scale }]}>
				<View style={styles.amountRow}>
					<Image
						source={JackpotChipPng}
						resizeMode='contain'
						style={{ width: 28 * scale, height: 28 * scale }}
					/>
					<Text style={[styles.amount, { fontSize: 28 * scale }]}>{formatAmount(amount)}</Text>
				</View>
				<CtaButton
					accessibilityLabel={t('T_SITE_STORE_BUY_CHIPS', {
						defaultValue: 'Buy {{amount}} chips',
						amount: formatAmount(amount),
					})}
					label={
						Number(pkg.price) === 0
							? t('T_SITE_SHOP_FREE', { defaultValue: 'FREE' }).toUpperCase()
							: formatPrice(pkg)
					}
					onPress={() => onBuy(pkg)}
					style={{ width: 168 * scale, minHeight: 36 * scale }}
					labelStyle={{ fontSize: 13 * labelScale }}
				/>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	root: { flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' },
	// Slight horizontal overscan fills the fixed-ratio modal while keeping the artwork top-aligned.
	vipBackground: {
		aspectRatio: 716 / 1196,
		left: '-4%',
		opacity: 0.55,
		position: 'absolute',
		top: '-30%',
		width: '108%',
	},
	header: { alignItems: 'center', justifyContent: 'center' },
	headerTitle: { alignItems: 'center', columnGap: 3, flexDirection: 'row' },
	title: {
		color: '#FFE275',
		fontFamily: 'Sofia Sans',
		fontWeight: '800',
		textShadowColor: 'rgba(255, 156, 0, 0.5)',
		textShadowOffset: { width: 0, height: 1 },
		textShadowRadius: 4,
	},
	titleWebGlow: {
		textShadowColor: 'rgba(255, 114, 0, 0.6)',
		textShadowOffset: { width: 0, height: 0 },
	},
	close: {
		alignItems: 'center',
		height: 32,
		justifyContent: 'center',
		position: 'absolute',
		width: 32,
	},
	tabs: { flexDirection: 'row' },
	tab: {
		alignItems: 'center',
		backgroundColor: '#180403',
		borderColor: '#562013',
		borderWidth: 1,
		flex: 1,
		flexDirection: 'row',
		justifyContent: 'center',
	},
	activeTab: { backgroundColor: '#A61609', borderColor: '#FF6A4D' },
	tabIcon: { resizeMode: 'contain' },
	tabLabel: { color: '#FFF1D2', fontFamily: 'Sofia Sans', fontWeight: '700' },
	activeTabLabel: { color: '#FFFF99' },
	scroll: { flex: 1, marginTop: 10, minHeight: 0 },
	packageList: { paddingRight: 14 },
	desktopPackageList: { flexDirection: 'row', flexWrap: 'wrap' },
	desktopPackageCell: { width: '48.5%' },
	inventoryBody: { alignItems: 'center', flex: 1, marginTop: 10, minHeight: 0 },
	inventoryTitle: {
		alignSelf: 'stretch',
		color: '#FFF5B9',
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
		marginHorizontal: 16,
		marginTop: 4,
	},
	inventorySubtitle: {
		alignSelf: 'stretch',
		color: '#FFF5B9',
		fontFamily: 'Sofia Sans',
		marginHorizontal: 16,
	},
	inventoryScroll: { alignSelf: 'stretch', flex: 1, marginTop: 10 },
	inventoryGrid: { alignSelf: 'center', flexDirection: 'row', flexWrap: 'wrap', paddingBottom: 32 },
	desktopInventoryGrid: { justifyContent: 'flex-start', paddingHorizontal: 16 },
	packageRow: {
		alignItems: 'center',
		backgroundColor: 'rgba(46, 10, 4, 0.92)',
		borderColor: '#E35C11',
		borderWidth: 1,
		flexDirection: 'row',
	},
	packageDetails: { alignItems: 'center', flex: 1 },
	amountRow: { alignItems: 'center', columnGap: 6, flexDirection: 'row' },
	amount: { color: '#FFFFA1', fontFamily: 'Sofia Sans', fontWeight: '800' },
	stateText: {
		color: '#FFF1D2',
		fontFamily: 'Sofia Sans',
		paddingVertical: 24,
		textAlign: 'center',
	},
	errorText: {
		color: '#FFAD9D',
		fontFamily: 'Sofia Sans',
		paddingVertical: 24,
		textAlign: 'center',
	},
});

export default StoreRedesignModal;
